import { defineStore } from 'pinia';

/** Who is assigned (roleId) to an assistance request of the application */
export interface ArAssignment {
  arId: string;
  /** Assistance type shown in the warning, e.g. "Electricity" */
  name: string;
  roleId: number | null;
}

/**
 * TASK 7294 — warns a caseworker before they change an application someone else is already working on.
 *
 * - On the AR accordion: warn when the current user is not the one assigned to that assistance request
 *   (even if they are assigned to another AR of the same application).
 * - Everywhere else on the application: warn when the current user is none of the assigned users.
 * - Shown once per application page open: confirming it silences it until the application is reopened.
 *
 * Edit triggers are guarded with the `v-assigned-warning` directive (plugins/7.assigned-warning.client.ts),
 * the modal itself lives on pages/applications/[id]/show.vue.
 */
export const useAssignedWarningStore = defineStore('assigned-warning', () => {
  const applicationId = ref<string | null>(null);
  const assignments = ref<ArAssignment[]>([]);
  const acknowledged = ref(false);
  const open = ref(false);
  let pendingAction: (() => void) | null = null;

  /** Fresh page open of an application (or `''` when leaving it): forget a previous confirmation */
  const reset = (appId: string) => {
    applicationId.value = appId || null;
    assignments.value = [];
    acknowledged.value = false;
    open.value = false;
    pendingAction = null;
  };

  const setAssignments = (appId: string, arAssignments: ArAssignment[]) => {
    if (applicationId.value !== appId) reset(appId);
    assignments.value = arAssignments;
  };

  const needsWarning = (arId?: string | null) => {
    if (acknowledged.value) return false;
    const currentRoleId = useUserStore().currentUser?.profile?.roleId;
    const assignedRoleIds = assignments.value.filter((_) => _.roleId !== null && _.roleId !== undefined);
    if (!assignedRoleIds.length) return false;

    const arAssignment = arId ? assignedRoleIds.find((_) => _.arId === arId) : undefined;
    if (arAssignment) return arAssignment.roleId !== currentRoleId;
    // not on an AR (or that AR is unassigned): warn only when the user is assigned to none of the ARs
    return !assignedRoleIds.some((_) => _.roleId === currentRoleId);
  };

  /** Runs `action` right away, or once the user confirms the warning */
  const guard = (action: () => void, arId?: string | null) => {
    if (!needsWarning(arId)) {
      action();
      return;
    }
    pendingAction = action;
    open.value = true;
  };

  const confirm = () => {
    acknowledged.value = true;
    open.value = false;
    const action = pendingAction;
    pendingAction = null;
    // let the modal close (and release its focus trap) before replaying the action
    if (action) nextTick(action);
  };

  const cancel = () => {
    open.value = false;
    pendingAction = null;
  };

  return { applicationId, assignments, acknowledged, open, reset, setAssignments, needsWarning, guard, confirm, cancel };
});
